import {formatDate} from 'date-fns';
import React, {useRef} from 'react';
import {cva, styles} from '../../Utilities';
import {FieldLabel, FieldLabelWithInfo} from '../_internal/FieldLabel/FieldLabel';

type triggerAttributes = React.ComponentProps<'input'>;

type CoreProps = Pick<triggerAttributes, 'disabled' | 'name' | 'onBlur' | 'onFocus'>;

type RenderableLabel =
    | React.ReactElement<{children: React.ReactNode}> // jsx can't be typed, but we can be pretty sure any children prop will satisfy ReactNode
    | string
    | null
    | undefined;

interface DatePickerProps extends CoreProps {
    label?: RenderableLabel;
    helperText?: React.ReactNode;
    onChange: (value: Date | undefined, event: React.ChangeEvent<HTMLInputElement>) => void;
    value: Date | undefined;
    isInvalid?: boolean;
    format?: string;
    min?: Date;
    max?: Date;
}

export function RsnDatePicker({
    label,
    helperText,
    isInvalid = false,
    disabled = false,
    onFocus,
    onBlur,
    name,
    onChange,
    value,
    format = 'dd MMMM yyyy',
    min,
    max,
}: DatePickerProps) {
    const inputRef = useRef<HTMLInputElement>(null);

    const handleButtonClick = () => {
        inputRef.current?.focus();
        inputRef.current?.showPicker();
    };

    function renderEmptyPlaceholder() {
        return <span className='fv-text-xl-regular text-shade-60'>–Select date–</span>;
    }

    return (
        <div className={cls.outerBox({disabled, isInvalid})}>
            {label}
            <div className='relative'>
                <input
                    ref={inputRef}
                    className={cls.input}
                    type='date'
                    name={name}
                    disabled={disabled}
                    value={value ? formatDate(value, 'yyyy-MM-dd') : ''}
                    onChange={e => {
                        onChange(e.target.value ? new Date(e.target.value) : undefined, e);
                    }}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    min={min ? formatDate(min, 'yyyy-MM-dd') : undefined}
                    max={max ? formatDate(max, 'yyyy-MM-dd') : undefined}
                />
                <button type='button' className={cls.button} onClick={handleButtonClick} disabled={disabled}>
                    <div className={cls.innerBox}>
                        {value ? formatDate(value, format) : renderEmptyPlaceholder()}
                        <div className={cls.decor}>
                            <CalendarIcon />
                        </div>
                    </div>
                </button>
            </div>
            <div className={cls.helper}>{helperText}</div>
        </div>
    );
}

export namespace RsnDatePicker {
    export const Label = FieldLabel;
    export const LabelWithInfo = FieldLabelWithInfo;
}

const CalendarIcon = React.memo(function CalendarIcon() {
    return (
        <svg
            xmlns='http://www.w3.org/2000/svg'
            width='1rem'
            height='1rem'
            fill='none'
            viewBox='0 0 24 24'
            className='text-neutral-90'
        >
            <g clipPath='url(#a)'>
                <path
                    fill='currentColor'
                    d='M17 3h4a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h4V1h2v2h6V1h2v2Zm-2 2H9v2H7V5H4v4h16V5h-3v2h-2V5Zm5 6H4v8h16v-8Z'
                />
            </g>
            <defs>
                <clipPath id='a'>
                    <path fill='#fff' d='M0 0h24v24H0z' />
                </clipPath>
            </defs>
        </svg>
    );
});

const cls = styles({
    button: [
        'fv-text-xl relative h-11 w-full rounded-sm border border-solid border-neutral-50 px-3',
        'text-neutral-90 ring-primary-20 focus:border-primary-30 focus:text-neutral-100 focus:ring',
        'disabled:border-neutral-50 disabled:bg-neutral-30 disabled:text-neutral-60',
        'text-left group-[.bad]:border-danger-30 group-[.bad]:ring-danger-20',
    ],
    outerBox: cva('group fv-text-m relative grid gap-2', {
        variants: {
            disabled: {true: 'off', false: ''},
            isInvalid: {true: '', false: ''},
        },
        compoundVariants: [{isInvalid: true, disabled: false, className: 'bad'}],
    }),
    innerBox: 'mx-0.5 flex items-center gap-2',
    input: `pointer-events-none absolute left-0 top-0 h-full w-full opacity-0`,
    helper: 'absolute left-0 top-full pt-1 text-neutral-60 group-[.bad]:text-danger-30',
    decor: 'ml-auto size-4 group-[.off]:hidden',
});
